import React from "react";

import clsx from "clsx";

import { Box, type BoxProps } from "~components/Box";

import * as styles from "./styles.css";

// Types for component props
type TextAppearance =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "body_lg"
  | "body_md"
  | "body_sm"
  | "body_xs";
type FontFamily = "default" | "alternate";

export interface TypographyProps
  extends Omit<BoxProps, "textAppearance" | "fontFamily" | "fontSize"> {
  /**
   * Defines the text appearance (font size, line height, font weight).
   * @example 'h1', 'body_lg'
   */
  textAppearance?: TextAppearance;
  /**
   * Selects the font family. 'default' is the predefined font family.
   * @default 'default'
   * @example 'alternate'
   */
  fontFamily?: FontFamily;
  /**
   * The text content to display.
   */
  children: React.ReactNode;
  /**
   * Specifies the HTML tag to use for rendering the text.
   * @default 'p'
   * @example 'h1', 'span', 'div'
   */
  as?: keyof JSX.IntrinsicElements;
  /**
   * Additional classes to apply to the component.
   */
  className?: string;
}

/**
 * A flexible Typography component for managing text appearance.
 * Allows choosing between different font sizes and weights, and between two font families.
 */
export function Typography({
  textAppearance,
  fontFamily,
  children,
  as = "p", // Defaults to <p> HTML tag
  className, // Prop for additional classes
  ...rest
}: TypographyProps) {
  // Generate the class name using the typography recipe based on props
  const typographyClassName = styles.typographyRecipe({
    textAppearance,
    fontFamily,
  });

  return (
    <Box as={as} className={clsx(typographyClassName, className)} {...rest}>
      {children}
    </Box>
  );
}
